import type { InboundMessage, TokenRequest } from "ably"
import { invariant } from "@holigral/shared/utils"
import { getAbly } from "./ably"

type EventPayloads = Record<string, unknown>
type EventHandler<TEventPayload> = (payload: TEventPayload) => void
type EventHandlers<T extends EventPayloads> = {
  [K in keyof T]: EventHandler<T[K]>
}

export default async <T extends EventPayloads>(
  channelName: string,
  getAblyTokenRequest: () => Promise<TokenRequest>,
  eventHandlers: EventHandlers<T>,
) => {
  const ably = await getAbly(getAblyTokenRequest)
  const channel = ably.channels.get(channelName)

  const unsubscribers = await Promise.all(
    Object.keys(eventHandlers).map(async (eventName) => {
      const handler = eventHandlers[eventName]
      invariant(handler, `No handler for event: ${eventName}`)

      const listener = (message: InboundMessage) => {
        handler(JSON.parse(message.data as string) as T[typeof eventName])
      }

      await channel.subscribe(eventName, listener)

      return () => {
        channel.unsubscribe(eventName, listener)
      }
    }),
  )

  return () => {
    unsubscribers.forEach((unsub) => {
      unsub()
    })
  }
}
